// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TextField, { TextFieldProps } from "@conklin/MobiusOverrides/TextField/TextField";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
    // XNG-Changes
    handleSearchFilterBrands?: (e: string) => void;
    searchFilterBrands?: string;
    selectedLetter?: string;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
    // XNG-Changes
    alphabetContainer?: InjectableCss;
    titleText?: TypographyPresentationProps;
    brandsLettersWrapper?: InjectableCss;
    alphabetSection?: InjectableCss;
    filterSection?: InjectableCss;
    filterInput?: TextFieldProps;
    selectWrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            text-transform: uppercase;
            justify-items: center;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            margin-bottom: 50px;
            margin-top: 20px;
        `,
    },
    navigationItem: {
        css: css`
            margin: 0;
            display: inline-block;
            text-align: center;
            flex: 0 0 45px;
        `,
    },
    navigationLink: {
        css: css``,
        typographyProps: {
            transform: "uppercase",
            variant: "headerTertiary",
            forwardAs: "span",
            color: "primary.contrast",
            css: css``,
        },
    },
    navigationTextEmpty: {
        transform: "uppercase",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "primary.contrast",
        css: css`
            color: ${getColor("common.ui8")} !important;
        `,
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [alphabet] = useState(() => [{ letter: "all", count: 0, linkable: true }, ...props.alphabet]);
    const [selectedLetter, setSelectedLetter] = useState(props.selectedLetter);
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));

    // XNG-Changes: To get selectedLetter change when a user click on clean Filter
    React.useEffect(() => {
        setSelectedLetter(props.selectedLetter);
    }, [props.selectedLetter]);

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };

    // XNG-Changes
    const createHandleClickOfLetter = (letter: string) => () => {
        setSelectedLetter(letter);
        if (props.searchFilterBrands && props.handleSearchFilterBrands) {
            props.handleSearchFilterBrands(props.searchFilterBrands);
        }
        return handleClickOfLetter(letter);
    };

    // XNG-Changes: we add `All` as a first element
    if (alphabet.length <= 1) {
        return null;
    }

    // XNG-Changes
    const handleSelectChange = (e: any) => {
        setSelectedLetter(e.target.value);
        return handleClickOfLetter(e.target.value);
    };

    // XNG-Changes: Add filter, change from # to 0-9
    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation" id="alphabetNavigation">
            <StyledWrapper {...styles.brandsLettersWrapper}>
                <StyledWrapper {...styles.alphabetSection}>
                    <Typography {...styles.titleText}>{translate("Filter Alphabetically")}</Typography>
                    <StyledWrapper {...styles.alphabetContainer}>
                        {alphabet.map(alphabetItem => (
                            <StyledSpan
                                {...styles.navigationItem}
                                key={alphabetItem.letter}
                                data-test-selector="alphabetNavigationItemLetter"
                            >
                                {alphabetItem.linkable ? (
                                    <Link
                                        {...styles.navigationLink}
                                        onClick={createHandleClickOfLetter(alphabetItem.letter)}
                                        aria-label={alphabetItem.letter}
                                        {...{
                                            selectedLetter:
                                                selectedLetter === alphabetItem.letter && alphabetItem.linkable,
                                            disabled: !alphabetItem.linkable,
                                        }}
                                    >
                                        {alphabetItem.letter === "#" ? translate("0-9") : alphabetItem.letter}
                                    </Link>
                                ) : (
                                    <Typography {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                                )}
                            </StyledSpan>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.selectWrapper}>
                    <Typography {...styles.titleText}>{translate("Filter Alphabetically")}</Typography>
                    <Select
                        {...styles.select}
                        placeholder={translate("Search by First Letter")}
                        onChange={(e: any) => handleSelectChange(e)}
                        value={selectedLetter || ""}
                        // label={translate("Filter Alphabetically")}
                    >
                        {/* <option disabled value="">
                            {translate("Search by First Letter")}
                        </option> */}
                        {alphabet.map(alphabetItem => (
                            <option
                                disabled={!alphabetItem.linkable}
                                key={alphabetItem.letter}
                                value={alphabetItem.letter}
                            >
                                {alphabetItem.letter === "#"
                                    ? translate("0-9")
                                    : alphabetItem.letter === "all"
                                      ? translate("Show All Brands")
                                      : alphabetItem.letter.toUpperCase()}
                            </option>
                        ))}
                    </Select>
                </StyledWrapper>

                <StyledWrapper {...styles.filterSection}>
                    <Typography {...styles.titleText}>{translate("Search by Name")}</Typography>
                    <TextField
                        name="filterBrands"
                        value={props.searchFilterBrands}
                        placeholder={translate("Search by Name")}
                        onChange={(e: any) => props.handleSearchFilterBrands?.(e.target.value)}
                        {...styles.filterInput}
                        // XNG-Changes
                        iconClickableProps={{
                            onClick: () => {},
                            "aria-label": "Search by Manufacturer Name",
                        }}
                        showClear={!!props.searchFilterBrands}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
